import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useTenant } from "@app/providers/tenant";
import { useCatalogWritePermission } from "./CatalogPermissions";
import { Button } from "@ui/core";
import { listProducts } from "../api/products";
import { parseApiError, isSensitiveMessage } from "@shared/lib/errors";

export const fieldClass =
  "w-full rounded-md border bg-background px-3 py-2 text-sm";
export type CatalogSearch = {
  q: string;
  productId: string;
  page: number;
  pageSize: number;
  isActive: string;
  answered: string;
  hasBarcode: string;
  rating: string;
};
export function parseCatalogSearch(
  input: Record<string, unknown>,
): CatalogSearch {
  const text = (key: string) =>
    typeof input[key] === "string" ? String(input[key]).slice(0, 512) : "";
  const flag = (key: string) =>
    [true, "true"].includes(input[key] as string)
      ? "true"
      : [false, "false"].includes(input[key] as string)
        ? "false"
        : "";
  const page = Number(input.page);
  const pageSize = Number(input.pageSize);
  return {
    q: text("q"),
    productId: text("productId"),
    page: Number.isSafeInteger(page) && page > 0 ? Math.min(page, 1000000) : 1,
    pageSize: [20, 50, 100, 200].includes(pageSize) ? pageSize : 20,
    isActive: flag("isActive"),
    answered: flag("answered"),
    hasBarcode: flag("hasBarcode"),
    rating: /^[1-5]$/.test(String(input.rating)) ? String(input.rating) : "",
  };
}
export const optionalBoolean = (value: string) =>
  value === "true" ? true : value === "false" ? false : undefined;
export function useCatalogSearch() {
  const location = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();
  const state = React.useMemo(
    () => parseCatalogSearch(location.search as Record<string, unknown>),
    [location.search],
  );
  const set = React.useCallback(
    (patch: Partial<CatalogSearch>) => {
      const search = { ...state, ...patch };
      void navigate({
        to: location.pathname,
        search: search as never,
        replace: false,
      });
    },
    [state, navigate, location.pathname],
  );
  return { state, set };
}
export function useCatalogContext() {
  const { tenantId } = useTenant();
  const canWrite = useCatalogWritePermission();
  const { t, i18n } = useTranslation("catalog");
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({
      predicate: (query) =>
        /^admin\.(labels|product|barcodes)/.test(String(query.queryKey[0])),
    });
  return {
    tenantId,
    canWrite,
    t,
    language: i18n.language,
    invalidate,
  };
}
export function ErrorNotice({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  const { t } = useTranslation("catalog");
  if (!error) return null;
  const info = parseApiError(error, t("failed"));
  const details = Object.values(info.errors || {})
    .flat()
    .filter((message) => !isSensitiveMessage(message));
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
    >
      <div>
        <p>{info.message}</p>
        {details.length > 0 && (
          <ul className="list-inside list-disc">
            {[...new Set(details)].map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        )}
      </div>
      {retry && (
        <Button variant="outline" onClick={retry}>
          {t("retry")}
        </Button>
      )}
    </div>
  );
}
export function CatalogSearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation("catalog");
  const [draft, setDraft] = React.useState(value);
  React.useEffect(() => setDraft(value), [value]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onChange(draft.trim());
      }}
      className="flex min-w-0 flex-wrap items-end gap-2"
    >
      <label className="min-w-0 flex-1 text-sm">
        {t("search")}
        <input
          className={fieldClass}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={512}
        />
      </label>
      <Button type="submit" variant="outline">
        {t("search")}
      </Button>
    </form>
  );
}
export function ProductFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t, tenantId } = useCatalogContext();
  const [search, setSearch] = React.useState("");
  const q = useQuery({
    queryKey: ["admin.products", "catalog-filter", tenantId, search],
    queryFn: () => listProducts({ q: search, page: 1, pageSize: 50 }),
    enabled: !!tenantId,
  });
  return (
    <details className="rounded-md border p-2 text-sm">
      <summary className="cursor-pointer">
        {t("product")}
        {value
          ? ` · ${q.data?.items.find((p) => p.id === value)?.name || value}`
          : ` · ${t("all")}`}
      </summary>
      <CatalogSearchField value={search} onChange={setSearch} />
      <label>
        {t("product")}
        <select
          className={fieldClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{t("all")}</option>
          {value && !q.data?.items.some((p) => p.id === value) && (
            <option value={value}>{value}</option>
          )}
          {q.data?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <ErrorNotice error={q.error} retry={() => void q.refetch()} />
    </details>
  );
}
export function CatalogPagination({
  page,
  pageSize,
  total,
  busy,
  set,
}: {
  page: number;
  pageSize: number;
  total: number;
  busy: boolean;
  set: (patch: Partial<CatalogSearch>) => void;
}) {
  const { t } = useTranslation("catalog");
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav
      aria-label={t("pagination")}
      className="flex flex-wrap items-center justify-between gap-3 text-sm"
    >
      <label>
        {t("pageSize")}{" "}
        <select
          className="rounded border bg-background p-2"
          value={pageSize}
          onChange={(e) => set({ pageSize: Number(e.target.value), page: 1 })}
        >
          {[20, 50, 100, 200].map((n) => (
            <option key={n}>{n}</option>
          ))}
        </select>
      </label>
      <span aria-live="polite">{t("pageOf", { page, pages, total })}</span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={busy || page <= 1}
          onClick={() => set({ page: page - 1 })}
        >
          {t("previous")}
        </Button>
        <Button
          variant="outline"
          disabled={busy || page >= pages}
          onClick={() => set({ page: page + 1 })}
        >
          {t("next")}
        </Button>
      </div>
    </nav>
  );
}
export function useClampCatalogPage(
  total: number | undefined,
  state: CatalogSearch,
  set: (patch: Partial<CatalogSearch>) => void,
  refreshing: boolean,
) {
  React.useEffect(() => {
    if (total === undefined || refreshing) return;
    const last = Math.max(1, Math.ceil(total / state.pageSize));
    if (state.page > last) set({ page: last });
  }, [total, state.page, state.pageSize, set, refreshing]);
}
